import { QuestionMedia } from "components/QuestionMedia/QuestionMedia";
import { QuestionInput } from "components/QuestionInput/QuestionInput";
import { ArrowRight } from "assets/svg";
import { disableCopyPasteHandlers } from "utils/helper";
import { useRef, useMemo, useEffect } from "react";
import classNames from "classnames";
import { QUESTION_TYPE } from "constant";
import { motion } from "framer-motion";
import { Timer } from "components/Timer/Timer";
import { QuizHeader } from "components/QuizHeader/QuizHeader";
import "./spotlight_quiz.css"

const SpotlightAudioQuiz = ({
    questions,
    activeQuestionId,
    optionSubmitHandler,
    timeLeft,
    sectionName,
    questionsCount,
    isQuizEnded,
    moveToNextGame
}) => {
    const cardRef = useRef()
    const questionContainerRef = useRef()
    const touchStartX = useRef(null)
    const touchEndX = useRef(null)

    const questionClass = classNames("my-[15px] text-[#fff] my-0 break-words w-[calc(80vw-36px)] sm:w-[calc(30vw-36px)] relative z-[1]", "text-[13px] font-light leading-[18px]")

    const [currentQuestion, isHtml, isBreaker] = useMemo(() => {
        const updatedQues = questions.find(ques => ques.id == activeQuestionId)
        return [
            updatedQues,
            /<\/?[a-z][\s\S]*>/i.test(updatedQues.question),
            [
                QUESTION_TYPE.BREAKER_SCREEN
            ].includes(updatedQues.type)
        ]
    }, [activeQuestionId])

    useEffect(() => {
        document.body.style.fontFamily = "Martian Mono SemiExpanded"
    }, [])

    useEffect(() => {
        if (questionContainerRef.current) {
            questionContainerRef.current.scrollTop = 0;
            questionContainerRef.current.scrollLeft = 0;
        }
    }, [activeQuestionId])

    useEffect(() => {
        if (isQuizEnded) {
            moveToNextGame()
        }
    }, [isQuizEnded])

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX
    };

    const handleTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].clientX
        handleSwipe()
    };

    const handleSwipe = () => {
        if (touchStartX.current && touchEndX.current) {
            const deltaX = touchEndX.current - touchStartX.current
            if (deltaX < 0) {
                goToNextQuestion()
            }
        }
        touchStartX.current = null
        touchEndX.current = null
    }

    const goToNextQuestion = (payload = {}, nextQues = true, markComplete = false) => {
        cardRef.current.classList.add("card-out")
        setTimeout(() => {
            optionSubmitHandler(payload, nextQues, markComplete)
        }, 420)
    }

    return (
        <div
            className={classNames(
                "quiz bg-[#000] sm:rounded-[20px] min-h-full sm:max-h-full sm:h-full",
            )}
        >
            {
                isQuizEnded
                    ? <div className="h-[80vh] w-full flex flex-col items-center justify-center">
                        <p className="text-[20px] font-light text-[#fff] capitalize">thank you for playing!</p>
                    </div>
                    : <>
                        <QuizHeader
                            type={currentQuestion.type}
                            sectionName={sectionName}
                            questionOrder={`Question ${currentQuestion?.count ?? ""}/${questionsCount}`}
                        >
                            <Timer time={timeLeft} type={currentQuestion.type} />
                        </QuizHeader>
                        <div className="flex items-center justify-end px-[20px]">
                            <span
                                onClick={goToNextQuestion}
                                className="rounded-full bg-[#262626] p-2 cursor-pointer"
                            >
                                <ArrowRight />
                            </span>
                        </div>
                        <div
                            ref={questionContainerRef}
                            className={classNames(
                                "flex flex-col justify-between px-[18px] pb-[26px] pt-4 h-[90vh] sm:h-[72vh] overflow-y-auto",
                            )}
                        >
                            <div className={classNames("flex items-center justify-center mt-[10px] relative")}>
                                <motion.div
                                    className={classNames("bg-[#262626] w-[80vw] sm:w-[30vw] h-[330px] sm:h-[450px] rounded-[10px] absolute top-0", {
                                        "h-[330px]": isBreaker
                                    })}
                                    initial={{ rotate: -5, scale: 0.98 }}
                                />
                                <motion.div
                                    className={classNames("bg-[#262626] w-[80vw] sm:w-[30vw] h-[330px] sm:h-[450px] rounded-[10px] absolute top-0", {
                                        "h-[330px]": isBreaker
                                    })}
                                    initial={{ rotate: 8, scale: 0.98 }}
                                />
                                {
                                    questions.map(ques => {
                                        if (ques.id === activeQuestionId) {
                                            return (
                                                <motion.div
                                                    ref={cardRef}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: [0, 1], scale: [0.98, 1] }}
                                                    transition={{ duration: 0.3 }}
                                                    className={classNames("w-[80vw] sm:w-[30vw] h-[330px] sm:h-[450px] overflow-y-auto rounded-[10px] px-[16px] py-[20px] bg-[#39728F] overflow-hidden relative z-[2]", {
                                                        "h-[330px]": isBreaker
                                                    })}
                                                    onTouchStart={handleTouchStart}
                                                    onTouchEnd={handleTouchEnd}
                                                >
                                                    <div className={classNames("",
                                                        "w-[272px] h-[300px] bg-[#4489A5] absolute rounded-full right-[-100px]")} />
                                                    {
                                                        currentQuestion?.media?.length > 0
                                                            ? <div className={classNames("relative z-[1] mb-[10px]")}>
                                                                <QuestionMedia
                                                                    media={currentQuestion?.media}
                                                                    containerHeight={"200px"}
                                                                />
                                                            </div> : <></>
                                                    }
                                                    {!isHtml ? (
                                                        <h2
                                                            className={questionClass}
                                                            {...disableCopyPasteHandlers}
                                                        >
                                                            {currentQuestion.question?.split("\n").map((sentence, index) =>
                                                                index > 0 ? (
                                                                    <>
                                                                        <br />
                                                                        {sentence}
                                                                    </>
                                                                ) : (
                                                                    sentence
                                                                )
                                                            )}
                                                        </h2>
                                                    ) : (
                                                        <div
                                                            className={classNames(questionClass, "html")}
                                                            dangerouslySetInnerHTML={{ __html: isHtml ? currentQuestion.question : null }}
                                                            {...disableCopyPasteHandlers}
                                                        />
                                                    )}
                                                </motion.div>
                                            )
                                        }
                                    })
                                }
                            </div>
                            {
                                isBreaker
                                    ? <div className="flex items-center justify-center mb-20">
                                        <button
                                            onClick={goToNextQuestion}
                                            className="flex items-center justify-center rounded-[120px] bg-[#fff] px-[10px] py-[8px] uppercase text-[#000] w-[150px] text-[16px] font-semibold">
                                            go ahead
                                        </button>
                                    </div>
                                    : <QuestionInput
                                        key={activeQuestionId}
                                        type={currentQuestion.type}
                                        questionId={activeQuestionId}
                                        showGridView={false}
                                        options={currentQuestion?.options}
                                        isOptionTextLong={false}
                                        onSubmit={goToNextQuestion}
                                        time={timeLeft}
                                        timeToSolve={currentQuestion?.timeToSolve ?? null}
                                    />
                            }
                        </div>
                    </>
            }
        </div>
    )
}

export default SpotlightAudioQuiz